import dayjs from 'dayjs';
import _ from 'lodash';
import 'dayjs/locale/fr';
import { Button, Message, Modal } from '@mairistem/composants';
import React from 'react';
import { useArreteJournalierAutomatique } from '@/hooks';
import { formatMonetaire } from '@/utils';

dayjs.locale('fr');

/**
 * ArreteAutomatiqueCreator
 *
 * Déclenche POST /arretes/automatique une seule fois par sélection de régie.
 * Affiche une modale de confirmation pour régisseur / régisseur suppléant.
 * Les agents de guichet déclenchent aussi l'appel mais ne voient pas la modale.
 */
const ArreteAutomatiqueCreator = () => {
  const { arreteCreated, clearArrete, run } = useArreteJournalierAutomatique({ mode: 'once' });

  // `run` change d'identité avec la régie / le mandat (deps du useCallback), donc le relancer
  // ici équivaut à l'ancien effet sur [regie?.identifiant, isAgent]. Le verrou anti-double-appel
  // et le skip embedded sont gérés dans le hook.
  React.useEffect(() => {
    run();
  }, [run]);

  const arreteCreatedMessage = React.useMemo<React.ReactNode>(() => {
    if (!arreteCreated) return null;

    const date = dayjs(arreteCreated.date).locale('fr').format('D MMMM YYYY');
    const { montantTotal } = arreteCreated;
    const sansEncaissement = _.isNil(montantTotal) || montantTotal === 0;

    if (sansEncaissement) {
      const montantAnnulation = formatMonetaire(arreteCreated.montantTotalAnnulation ?? 0, 2, true);

      return (
        <>
          {'La journée du '}
          <strong>{date}</strong>
          {' est arrêtée sans encaissement.'}
          <br />
          {'Des annulations de reçus ont été saisies pour un montant de '}
          <strong>{montantAnnulation}</strong>.
        </>
      );
    }

    const montant = formatMonetaire(montantTotal, 2, true);

    return (
      <>
        {'La journée du '}
        <strong>{date}</strong>
        {' est arrêtée pour la somme de '}
        <strong>{montant}</strong>.
      </>
    );
  }, [arreteCreated]);

  if (!arreteCreated) return null;

  return (
    <Modal open size="small" onClose={clearArrete} closeIcon>
      <Modal.Header>Arrêté journalier</Modal.Header>
      <Modal.Content>
        <Message info>
          <Message.Header>{arreteCreatedMessage}</Message.Header>
        </Message>
      </Modal.Content>
      <Modal.Actions>
        <Button content="OK" onClick={clearArrete} positive />
      </Modal.Actions>
    </Modal>
  );
};

export default ArreteAutomatiqueCreator;
